import { useCallback, useEffect, useState } from "react";
import * as api from "../api/client";
import ChatTab from "./dashboard/ChatTab";
import FormsTab from "./dashboard/FormsTab";
import Overview from "./dashboard/Overview";
import SystemTab from "./dashboard/SystemTab";
import VerifyTab from "./dashboard/VerifyTab";
import VisualTab from "./dashboard/VisualTab";
import { attentionOf, BTN } from "./dashboard/lib";
import { Alert } from "./dashboard/ui";

const TABS = [
  ["umumiy", "Umumiy"],
  ["suhbat", "Suhbat"],
  ["juftlik", "Juftlik tekshiruvi"],
  ["tasvir", "Fotodan tasvir"],
  ["savolnoma", "Savolnoma"],
  ["tizim", "Tizim"],
];

const PERIODS = [
  [0, "Hammasi"],
  [1, "Bugun"],
  [7, "7 kun"],
  [30, "30 kun"],
];

const DAYS_KEY = "ekspertiza.dashboard.days";

// `#juftlik/disagree` -> yorliq va uning boshlang'ich filtri
const fromHash = () => {
  const [tab, preset = ""] = window.location.hash.slice(1).split("/");
  return TABS.some(([k]) => k === tab) ? { tab, preset } : { tab: "umumiy", preset: "" };
};

const settle = (r) => (r.status === "fulfilled" ? r.value : null);

/** Natijalar: suhbat, juftlik tekshiruvi, tasvir, savolnoma va tizim holati bir sahifada. */
export default function Dashboard() {
  const [{ tab, preset }, setView] = useState(fromHash);
  const [days, setDays] = useState(() => {
    try {
      return Number(localStorage.getItem(DAYS_KEY)) || 0;
    } catch {
      return 0;
    }
  });
  const [refresh, setRefresh] = useState(0);
  const [rated, setRated] = useState(0);
  const [sum, setSum] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const on = () => setView(fromHash());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);

  const go = useCallback((t, p = "") => {
    window.location.hash = p ? `${t}/${p}` : t;
  }, []);

  const pickDays = (d) => {
    setDays(d);
    try {
      localStorage.setItem(DAYS_KEY, String(d));
    } catch {
      /* xususiy rejim */
    }
  };

  // Bir bo'lim ishlamasa qolganlari ko'rinaveradi
  useEffect(() => {
    let live = true;
    Promise.allSettled([
      api.reviewStats({ days }),
      api.verifyStats({ days }),
      api.reviewVisuals({ days, kind: "wrong", limit: 2 }),
      api.reviewForms({ days, limit: 1 }),
      api.llmStatus(),
      api.reviewTurns({ unrated: true, limit: 3, days }),
      api.verifyTurns({ kind: "disagree", limit: 3, days }),
    ]).then((rs) => {
      if (!live) return;
      const [chat, verify, visual, forms, status, unrated, disagree] = rs.map(settle);
      const failed = rs.find((r) => r.status === "rejected");
      setError(failed ? failed.reason?.message || String(failed.reason) : "");
      setSum({
        chat, verify, visual, forms, status,
        attention: attentionOf({
          unrated: unrated?.items,
          disagree: disagree?.items,
          wrongVisuals: visual?.items,
        }),
      });
    });
    return () => {
      live = false;
    };
  }, [days, refresh, rated]);

  const badge = {
    suhbat: sum?.chat?.unrated,
    juftlik: sum?.verify?.disagree,
    tasvir: sum?.visual?.stats.feedback.identity_wrong,
  };

  const exportJson = () => {
    const { attention: _, ...data } = sum ?? {};
    const blob = new Blob([JSON.stringify({ days, at: new Date().toISOString(), ...data }, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aiexpert-natijalar-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const props = { days, refresh, preset, onChanged: () => setRated((n) => n + 1) };

  return (
    <div className="min-h-full bg-[#072147] font-sans text-slate-100">
      <div className="mx-auto max-w-6xl space-y-4 px-4 py-6 sm:px-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-bold tracking-tight text-white sm:text-2xl">Natijalar</h1>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={days}
              onChange={(e) => pickDays(Number(e.target.value))}
              aria-label="Davr"
              className={BTN}
            >
              {PERIODS.map(([d, label]) => (
                <option key={d} value={d}>
                  {label}
                </option>
              ))}
            </select>
            <button onClick={() => setRefresh((n) => n + 1)} className={BTN}>
              Yangilash
            </button>
            <button onClick={exportJson} disabled={!sum} className={BTN}>
              JSON eksport
            </button>
            <a href="/" className={BTN}>
              Suhbatga qaytish
            </a>
          </div>
        </header>

        <nav className="flex gap-1 overflow-x-auto border-b border-sky-800/40">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => go(key)}
              className={`-mb-px whitespace-nowrap border-b-2 px-3.5 py-2 text-xs transition-colors ${
                tab === key ? "border-sky-400 text-white" : "border-transparent text-sky-400/60 hover:text-sky-200"
              }`}
            >
              {label}
              {badge[key] > 0 && (
                <span className="ml-1.5 rounded-full bg-rose-500/20 px-1.5 text-[10px] text-rose-300">{badge[key]}</span>
              )}
            </button>
          ))}
        </nav>

        {tab === "umumiy" && <Alert>{error}</Alert>}

        {/* `key`: «Diqqat talab» dan kelganda filtr shu bilan boshlanadi */}
        {tab === "umumiy" &&
          (sum ? <Overview s={sum} go={go} /> : <div className="py-10 text-center text-xs text-sky-400/60">yuklanmoqda...</div>)}
        {tab === "suhbat" && <ChatTab key={preset} {...props} />}
        {tab === "juftlik" && <VerifyTab key={preset} {...props} />}
        {tab === "tasvir" && <VisualTab key={preset} {...props} />}
        {tab === "savolnoma" && <FormsTab key={preset} {...props} />}
        {tab === "tizim" && (
          <SystemTab status={sum?.status} error={sum && !sum.status ? "Tizim holati olinmadi" : ""} />
        )}
      </div>
    </div>
  );
}
